// AppErrorBoundary — top-level safety net. Wraps the entire app tree in
// main.tsx. Catches anything that escapes Suspense + the lazy retry
// helper, including:
//   - Synchronous render errors from any page component
//   - Async errors that bubbled up past the inner boundaries
//   - The "reloading…" sentinel thrown by lazyWithRetry's last-resort
//     path, so the user sees a friendly "Updating to the latest
//     version" UI instead of a blank screen while the reload completes.
//
// Layout:
//   - Centered card with brand styling
//   - "Reload" + "Go home" buttons
//   - Collapsible technical details for support tickets

import { Component, type ReactNode } from "react";

interface Props { children: ReactNode }
interface State { error: Error | null; reloading: boolean }

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): State {
    // If this is the lazyWithRetry reload sentinel, surface a friendly
    // "Updating" state while the location.reload() finishes.
    const isReloadSentinel = /Reloading for new app version/i.test(
      error.message || "",
    );
    return { error, reloading: isReloadSentinel };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }): void {
    // Best-effort report. The track endpoint accepts arbitrary events,
    // so this doubles as a Sentry-lite for client errors.
    try {
      fetch("/api/track", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          event: "client_error",
          props: {
            message: error.message,
            stack: (error.stack || "").slice(0, 2000),
            componentStack: (info.componentStack || "").slice(0, 2000),
            path: window.location.pathname,
          },
        }),
      }).catch(() => { /* fire-and-forget */ });
    } catch { /* never throw from the boundary */ }

    // Auto-wipe persisted client state the moment the boundary fires.
    // User reported a crash loop: "anytime i open fieldkrew it does the
    // same error now." That means something persisted (IDB cache,
    // localStorage, a stale field in a cached API response) is the
    // trigger, and re-rendering with it intact will just re-crash.
    // Clearing now means by the time the user taps "Go to dashboard"
    // the next render starts from a clean slate. We preserve cookies
    // and keychain credentials so a logged-in user doesn't have to
    // retype their password on recovery.
    try {
      navigator.serviceWorker?.controller?.postMessage("fk:clear-cache");
    } catch { /* best-effort */ }
    try {
      // localStorage: keep ONLY whitelisted keys (crew mode preference,
      // language). Everything else (cache markers, mid-flight wizard
      // state, demo flags) gets wiped.
      const keep = new Set(["fk_crew_mode", "fk_lang"]);
      const saved: Array<[string, string]> = [];
      for (const k of Object.keys(localStorage)) {
        if (keep.has(k)) saved.push([k, localStorage.getItem(k) ?? ""]);
      }
      localStorage.clear();
      for (const [k, v] of saved) localStorage.setItem(k, v);
    } catch { /* best-effort */ }
    try { sessionStorage.clear(); } catch { /* best-effort */ }
    try {
      // Drop every IndexedDB database. This is the IDB cache lib's
      // store + anything else we've stashed (e.g., MapLibre tiles).
      void indexedDB.databases?.().then((dbs) => {
        for (const db of dbs) {
          if (db.name) indexedDB.deleteDatabase(db.name);
        }
      });
    } catch { /* best-effort */ }
  }

  // Stay on the current origin. Earlier this navigated to absolute
  // fieldkrew.com URLs on the assumption the local Capacitor bundle
  // was broken — but it turns out crossing origins drops the user's
  // session (cookies set on fieldkrew.com aren't read by the local
  // WebView's session-check pattern, so /app there bounces to /signin
  // and reads as "signed me out"). The Capacitor WebView treats
  // relative paths as SPA routes via its index.html fallback, so
  // relative navigation keeps the user in the native shell and signed
  // in. The auto-wipe in componentDidCatch already cleared persisted
  // state, so a relative reload starts fresh anyway.
  private reload = () => {
    window.location.replace("/app");
  };

  private signOutAndReset = async () => {
    try {
      await fetch("/api/auth/signout", {
        method: "POST",
        credentials: "include",
      });
    } catch { /* best-effort */ }
    window.location.replace("/signin");
  };

  render() {
    if (!this.state.error) return this.props.children;

    if (this.state.reloading) {
      return (
        <div style={shellStyle}>
          <div style={cardStyle}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0c1428" }}>
              Updating FieldKrew…
            </div>
            <div style={{ marginTop: 8, color: "#1e2a4a", fontSize: 14 }}>
              We just shipped a new version. One moment.
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={shellStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#0c1428" }}>
            Something went wrong loading this page.
          </div>
          <p style={{ marginTop: 8, color: "#1e2a4a", fontSize: 14, lineHeight: 1.5 }}>
            This is usually a temporary glitch. Try reloading; if it
            keeps happening, head back to the dashboard or contact
            support.
          </p>
          <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 8 }}>
            <button
              type="button"
              onClick={this.reload}
              style={primaryBtn}
            >
              Go to dashboard
            </button>
            <button
              type="button"
              onClick={() => void this.signOutAndReset()}
              style={secondaryBtn}
            >
              Sign out &amp; start over
            </button>
          </div>
          <details open style={{ marginTop: 16 }}>
            <summary style={{ cursor: "pointer", color: "#1e2a4a", fontSize: 12 }}>
              Technical details
            </summary>
            <pre
              style={{
                marginTop: 8,
                padding: 10,
                background: "#f5f6fa",
                border: "1px solid #e2e6f0",
                borderRadius: 6,
                fontSize: 11,
                overflow: "auto",
                maxHeight: 200,
                color: "#0c1428",
              }}
            >
              {this.state.error.message}
              {"\n\n"}
              {this.state.error.stack}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

const shellStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  background: "linear-gradient(135deg, #0c1428 0%, #1e2a4a 100%)",
};
const cardStyle: React.CSSProperties = {
  maxWidth: 480,
  width: "100%",
  background: "#fff",
  borderRadius: 16,
  padding: 24,
  boxShadow: "0 12px 48px rgba(12, 20, 40, 0.4)",
};
const primaryBtn: React.CSSProperties = {
  background: "#f26a1f",
  color: "#fff",
  border: 0,
  padding: "10px 18px",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 14,
};
const secondaryBtn: React.CSSProperties = {
  background: "#f5f6fa",
  color: "#0c1428",
  border: "1px solid #e2e6f0",
  padding: "10px 18px",
  borderRadius: 8,
  fontWeight: 600,
  textDecoration: "none",
  fontSize: 14,
  display: "inline-flex",
  alignItems: "center",
};
